import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  FormGroup,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { Node } from 'reactflow';

import { ReadWriteType, StateEntryProps } from '../../nodes';
import { ListEditorDialog } from '../ListEditorDialog/ListEditorDialog';

import { PropertyHeaderLine } from './PropertyHeadLine';
import { GetValueFromSelect } from './GetValueFromSelect';

export interface EditableMultiselectElementsInputProps {
  entry: StateEntryProps;
  updateElementValues: Function;
  updateElementValuesAutoGeneration: Function;
  nodes: Node<any>[];
}

export const EditableMultiselectElementsInput: React.FC<
  EditableMultiselectElementsInputProps
> = ({
  entry,
  updateElementValues,
  updateElementValuesAutoGeneration,
  nodes,
}) => {
  const intl = useIntl();

  const [autoGenerate, setAutoGenerate] = useState<boolean>(
    !(
      entry.payload.getLabelsFrom === undefined ||
      entry.payload.getLabelsFrom.trim() === ''
    )
  );

  const toggleMultiselectEntryElementsGeneration = () => {
    setAutoGenerate(!autoGenerate);
  };

  const elements = entry.payload.elements ?? [];

  const [isListEditorOpen, setIsListEditorOpen] = useState<boolean>(false);

  const [getElementsVariable, setGetElementsVariable] = useState<string>(
    entry.payload.getLabelsFrom ?? ''
  );

  const updateAutoGenValueSeries = (value: string, _index: number) => {
    setGetElementsVariable(value);
    updateElementValuesAutoGeneration(value);
  };

  const handleListEditorDialog = () => {
    setIsListEditorOpen(!isListEditorOpen);
  };

  const v2JSX = (
    <Typography variant="body2" component="span">
      {intl.formatMessage({
        id: 'builder.moduleeditor.propertiessidebar.polarcharinput.autoGenSeries.hint',
        defaultMessage: 'Liste für Werte',
      })}
      <GetValueFromSelect
        updateValueFrom={updateAutoGenValueSeries}
        currentValue={getElementsVariable}
        getValueType={ReadWriteType.stringArray}
        nodes={nodes}
      />
    </Typography>
  );

  const v1JSX = (
    <>
      <Button color="secondary" onClick={handleListEditorDialog}>
        {intl.formatMessage({
          id: 'builder.moduleeditor.propertiessidebar.editableMultiselectElementsInput.ownlistdialog',
          defaultMessage: 'Listeneinträge editieren',
        })}
      </Button>
    </>
  );

  const updateElements = (
    listelements: Array<any>,
    _listkeys?: string[],
    storeForUndo: boolean = true
  ) => {
    updateElementValues(listelements, storeForUndo);
  };

  return (
    <div>
      <PropertyHeaderLine
        header={intl.formatMessage({
          id: 'builder.moduleeditor.propertiesSidebar.editableMultiselectElementsInput.header',
          defaultMessage: 'Listeneinträge',
        })}
      />
      <br />
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={autoGenerate}
              onChange={toggleMultiselectEntryElementsGeneration}
              key="multiselectEntryElementsAutoGenCheckbox"
              id="multiselectEntryElementsAutoGenCheckboxId"
            />
          }
          label={
            <Typography variant="body2" component="span">
              {intl.formatMessage({
                id: 'builder.moduleeditor.propertiessidebar.multiselectEntryElements.checkboxtext',
                defaultMessage: 'Werte aus Variablen generieren',
              })}
            </Typography>
          }
        />
      </FormGroup>
      {autoGenerate && v2JSX}
      {!autoGenerate && v1JSX}
      <ListEditorDialog
        listelements={elements}
        open={isListEditorOpen}
        onClose={handleListEditorDialog}
        updateElements={updateElements}
        nodeType={entry.nodeType}
      />
    </div>
  );
};
